import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";

import ScrollReveal from "@/components/ScrollReveal";
import { useRealtimeInvalidate } from "@/hooks/useRealtimeInvalidate";

import jessAbout from "@/assets/jess-kitchen.jpg";
import jessSalad from "@/assets/jess-salad.webp";
import cookbookCover from "@/assets/cookbook-cover.webp";
import bookCoverFull from "@/assets/book-cover-full.webp";
import foodBreakfast from "@/assets/food-breakfast.webp";
import foodSnacks from "@/assets/food-snacks.webp";
import foodSalads from "@/assets/food-salads.webp";
import foodDinner from "@/assets/food-dinner.webp";
import foodDessert from "@/assets/food-dessert.webp";
import foodMixes from "@/assets/food-mixes.webp";
import jessGma from "@/assets/jess-gma.webp";
import SocialLinksPopover from "@/components/SocialLinksPopover";
import PageMeta from "@/components/PageMeta";
import { optimizeStorageUrl } from "@/lib/optimizeStorageUrl";
import { formatMinutes } from "@/lib/formatTime";
import HomepageSeasonalBlock from "@/components/HomepageSeasonalBlock";
import AdSlot from "@/components/AdSlot";
import StarRatingDisplay from "@/components/StarRatingDisplay";

const categoryFallbacks: Record<string, string> = {
  breakfast: foodBreakfast,
  snacks: foodSnacks,
  salads: foodSalads,
  dinner: foodDinner,
  dessert: foodDessert,
  mixes_spices: foodMixes,
};

const categoryCardImages: Record<string, string> = {
  breakfast: "https://ccpgzpcvxrjefmrguyks.supabase.co/storage/v1/object/public/site-images/recipes/breakfast-cookies/hero.jpg",
  snacks: "https://ccpgzpcvxrjefmrguyks.supabase.co/storage/v1/object/public/site-images/recipes/cheeseburger-egg-rolls/hero.jpg",
  salads: "https://ccpgzpcvxrjefmrguyks.supabase.co/storage/v1/object/public/site-images/recipes/street-corn-chicken-salad/street-corn-chicken-salad-057.jpg",
  dinner: "https://ccpgzpcvxrjefmrguyks.supabase.co/storage/v1/object/public/site-images/recipes%2Fhowdy-cowboy-brussels-sprout-bowls%2Fbowl1.jpg",
  dessert: "https://ccpgzpcvxrjefmrguyks.supabase.co/storage/v1/object/public/site-images/recipes/crumb-grabber-ice-cream-sandwiches/featured.jpg",
  mixes_spices: foodMixes,
  "quick-weeknight": "https://ccpgzpcvxrjefmrguyks.supabase.co/storage/v1/object/public/site-images/recipes%2Fsalsa-chicken-quesadillas%2Fhero.jpg",
  chicken: "https://ccpgzpcvxrjefmrguyks.supabase.co/storage/v1/object/public/site-images/recipes/bikini-bottomless-kabobs/featured.jpg",
  "air-fryer": "https://ccpgzpcvxrjefmrguyks.supabase.co/storage/v1/object/public/site-images/recipes%2Fpepperoni-pizza-twister-dogs%2Fhero.jpg",
  "comfort-food": "https://ccpgzpcvxrjefmrguyks.supabase.co/storage/v1/object/public/site-images/recipes%2Fchicken-tortilla-soup%2Fhero.jpg",
  holiday: "https://ccpgzpcvxrjefmrguyks.supabase.co/storage/v1/object/public/site-images/recipes%2Fpineapple-upside-down-baby-bundt-cakes%2Fhero.jpg",
};

const categoriesMeta = [
  { name: "Breakfast", desc: "Fast, filling ideas to keep mornings simple.", link: "breakfast" },
  { name: "Snacks", desc: "Quick bites when hunger hits between meals.", link: "snacks" },
  { name: "Salads", desc: "Fresh bowls packed with bold flavors.", link: "salads" },
  { name: "Dinner", desc: "Easy meals for those busy weeknights.", link: "dinner" },
  { name: "Dessert", desc: "Sweet endings with zero fuss required.", link: "dessert" },
  { name: "Mixes/Spices", desc: "Homemade blends to level up any dish.", link: "mixes_spices", mobileOnly: true },
];

// Extra category hubs — mirror the /recipes page so more of the catalog is
// surfaced from the homepage.
const extraHubs = [
  {
    name: "Quick Weeknight",
    desc: "30 minutes or less, dinner done.",
    href: "/recipes/category/quick-weeknight",
    img: categoryCardImages["quick-weeknight"],
  },
  {
    name: "Chicken",
    desc: "The MVP of the meal rotation.",
    href: "/recipes/category/chicken",
    img: categoryCardImages.chicken,
  },
  {
    name: "Air Fryer",
    desc: "Crispy, easy, barely any cleanup.",
    href: "/recipes/category/air-fryer",
    img: categoryCardImages["air-fryer"],
  },
  {
    name: "Comfort Food",
    desc: "Cozy meals that hit every time.",
    href: "/recipes/category/comfort-food",
    img: categoryCardImages["comfort-food"],
  },
  {
    name: "Holiday",
    desc: "Crowd-pleasers for every celebration.",
    href: "/recipes/category/holiday",
    img: categoryCardImages.holiday,
  },
];

const logoKey = import.meta.env.VITE_LOVABLE_CONNECTOR_LOGO_DEV_API_KEY as string | undefined;
const pressOutlets = [
  { name: "Good Morning America", domain: "goodmorningamerica.com" },
  { name: "The Drew Barrymore Show", domain: "thedrewbarrymoreshow.com" },
  { name: "BuzzFeed", domain: "buzzfeed.com" },
  { name: "Upworthy", domain: "upworthy.com" },
];

const Index = () => {
  const { data: blogPosts = [] } = useQuery({
    queryKey: ["blog-posts-home"],
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
    queryFn: async () => {
      const { data } = await supabase
        .from("blog_posts")
        .select("id, slug, title, featured_image_url, excerpt, content, is_roundup, roundup_recipe_slugs")
        .eq("is_published", true)
        .order("published_at", { ascending: false, nullsFirst: false })
        .limit(20);
      // Exclude roundup collections — those have their own "Recipe Roundups" section
      return (data ?? [])
        .filter((p: any) =>
          p.is_roundup !== true &&
          !(Array.isArray(p.roundup_recipe_slugs) && p.roundup_recipe_slugs.length > 0)
        )
        .slice(0, 3);
    },
  });
  useRealtimeInvalidate("blog_posts", [["blog-posts-home"]]);

  // New Recipes — most recently published
  const { data: newRecipes = [] } = useQuery({
    queryKey: ["recipes-home-new"],
    queryFn: async () => {
      const { data } = await supabase
        .from("recipes")
        .select("id, slug, title, featured_image_url, prep_time, cook_time")
        .eq("is_published", true)
        .order("published_at", { ascending: false, nullsFirst: false })
        .limit(4);
      return data ?? [];
    },
  });

  // Trending Recipes — most viewed in the last 7 days
  const { data: trendingRecipes = [] } = useQuery({
    queryKey: ["recipes-home-trending"],
    queryFn: async () => {
      const { data } = await supabase.rpc("get_trending_recipes" as any, { _days: 7, _lim: 12 });
      const rows = (data ?? []) as any[];
      if (rows.length >= 12) return rows;
      // Fallback until we have enough view data: random published recipes
      const { data: all } = await supabase
        .from("recipes")
        .select("id, slug, title, featured_image_url, prep_time, cook_time")
        .eq("is_published", true)
        .limit(60);
      const pool = (all ?? []).filter((r: any) => !rows.some((x) => x.id === r.id));
      for (let i = pool.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [pool[i], pool[j]] = [pool[j], pool[i]];
      }
      return [...rows, ...pool].slice(0, 12);
    },
  });

  // Top Recipes — most reviews + approved comments (broken by avg rating)
  const { data: topRecipes = [] } = useQuery({
    queryKey: ["recipes-home-top"],
    queryFn: async () => {
      const { data } = await supabase.rpc("get_top_recipes" as any, { _lim: 12 });
      return (data ?? []) as any[];
    },
  });

  // Keep the three recipe rows showing distinct recipes
  const newRow = (newRecipes as any[]).slice(0, 4);
  const trendingRow = (trendingRecipes as any[])
    .filter((r: any) => !newRow.some((n: any) => n.id === r.id))
    .slice(0, 4);
  const topRow = (topRecipes as any[])
    .filter((r: any) => !newRow.some((n: any) => n.id === r.id) && !trendingRow.some((t: any) => t.id === r.id))
    .slice(0, 4);

  // Upcoming events
  const { data: upcomingEvents = [] } = useQuery({
    queryKey: ["events-home-upcoming"],
    queryFn: async () => {
      const { data } = await supabase
        .from("events")
        .select("id, title, location, event_date, image_url, ticket_url, price, sold_out")
        .eq("is_published", true)
        .gte("event_date", new Date().toISOString())
        .order("event_date", { ascending: true })
        .limit(2);
      return data ?? [];
    },
  });

  // Roundup blog posts (posts with a non-empty roundup_recipe_slugs array)
  const { data: roundupPosts = [] } = useQuery({
    queryKey: ["blog-posts-roundups-home"],
    queryFn: async () => {
      const { data } = await supabase
        .from("blog_posts")
        .select("id, slug, title, featured_image_url, excerpt, roundup_recipe_slugs, is_roundup")
        .eq("is_published", true)
        .order("published_at", { ascending: false, nullsFirst: false })
        .limit(20);
      return (data ?? []).filter((p: any) =>
        p.is_roundup === true || (Array.isArray(p.roundup_recipe_slugs) && p.roundup_recipe_slugs.length > 0)
      ).slice(0, 3);
    },
  });

  // Recent 5-star reviews (joined with recipe title/slug)
  const { data: recentReviews = [] } = useQuery({
    queryKey: ["reviews-home-recent"],
    queryFn: async () => {
      const { data: reviews } = await supabase
        .from("public_reviews" as any)
        .select("id, recipe_id, author_name, rating, content, created_at")
        .gte("rating", 5)
        .order("created_at", { ascending: false })
        .limit(12);
      const rows = (reviews ?? []) as any[];
      if (rows.length === 0) return [];
      const ids = Array.from(new Set(rows.map((r) => r.recipe_id).filter(Boolean)));
      const { data: recipes } = await supabase
        .from("recipes")
        .select("id, slug, title")
        .in("id", ids)
        .eq("is_published", true);
      const byId = new Map((recipes ?? []).map((r: any) => [r.id, r]));
      const enriched = rows
        .map((r) => ({ ...r, recipe: byId.get(r.recipe_id) }))
        .filter((r) => r.recipe && (r.content || "").trim().length > 20);
      return enriched.slice(0, 3);
    },
  });

  // Shop strip — a few active shop products
  const { data: shopStrip = [] } = useQuery({
    queryKey: ["shop-strip-home"],
    queryFn: async () => {
      const { data } = await (supabase.from("shop_products" as any)
        .select("id, name, slug, image_url, affiliate_url, section, sort_order")
        .eq("is_active", true)
        .order("sort_order", { ascending: true })
        .limit(8));
      return (data ?? []) as any[];
    },
  });

  const renderRecipeRow = (
    eyebrow: string,
    title: string,
    subtitle: string,
    items: any[],
    bg: string,
    dark: boolean = false,
  ) => {
    if (!items || items.length === 0) return null;
    const headingColor = dark ? "text-[hsl(var(--nav-foreground))]" : "";
    const subColor = dark ? "text-[hsl(var(--nav-foreground))]/70" : "text-muted-foreground";
    const btnClass = dark
      ? "font-heading uppercase tracking-wide rounded-full bg-card/20 border-primary/50 text-primary hover:bg-card/30 text-sm"
      : "font-heading uppercase tracking-wide rounded-full border-foreground/60 text-foreground hover:bg-foreground/5 text-sm";
    return (
      <section className={`${bg} py-8 md:py-10 border-t border-border`}>
        <div className="container">
          <ScrollReveal className="text-center mb-6">
            <p className="font-handwritten text-primary text-2xl mb-1">{eyebrow}</p>
            <h2 className={`font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight mb-2 ${headingColor}`}>{title}</h2>
            <p className={`${subColor} font-body text-sm max-w-lg mx-auto`}>{subtitle}</p>
          </ScrollReveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {items.map((r, i) => {
              const totalTime = (r.prep_time || 0) + (r.cook_time || 0);
              return (
                <ScrollReveal key={r.id} delay={i * 0.06}>
                  <Link to={`/recipes/${r.slug}`} className="block group">
                    <div className="relative rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                      <div className="aspect-square overflow-hidden">
                        <img
                          src={optimizeStorageUrl(r.featured_image_url || foodDinner, 400, 400)}
                          alt={r.title}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                          onError={(e) => { e.currentTarget.src = foodDinner; }}
                        />
                      </div>
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-foreground/90 via-foreground/60 to-transparent px-3 pt-8 pb-2.5 md:px-4 md:pb-3">
                        <h3 className="font-heading text-xs md:text-base font-bold uppercase tracking-wide leading-snug text-background line-clamp-2">{r.title}</h3>
                        {totalTime > 0 && <p className="font-body text-[10px] md:text-xs text-background/80 mt-0.5">⏱ {totalTime} min</p>}
                      </div>
                    </div>
                  </Link>
                </ScrollReveal>
              );
            })}
          </div>
          <ScrollReveal className="text-center mt-8">
            <Button asChild variant="outline" className={btnClass}>
              <Link to="/recipes">View All Recipes →</Link>
            </Button>
          </ScrollReveal>
        </div>
      </section>
    );
  };

  const [nlName, setNlName] = useState("");
  const [nlEmail, setNlEmail] = useState("");
  const [nlSubmitting, setNlSubmitting] = useState(false);
  const [nlDone, setNlDone] = useState(false);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nlName.trim() || !nlEmail.trim()) return;
    setNlSubmitting(true);
    try {
      const res = await supabase.functions.invoke("subscribe-newsletter", {
        body: { name: nlName.trim(), email: nlEmail.trim() },
      });
      if (res.error) throw res.error;
      setNlDone(true);
    } catch {
      // silently fail for now
    } finally {
      setNlSubmitting(false);
    }
  };

  return (
    <div>
      <PageMeta
        title="Of Course It's Good - Bold Recipes by Jessica Secrest"
        description="Quick, practical recipes for busy days — flavor-packed meals without the extra fuss. By Jessica Secrest."
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Of Course It's Good",
          url: "https://ofcourseitsgood.com",
          description: "Bold, honest recipes and cooking by Jessica Secrest.",
          author: { "@type": "Person", name: "Jessica Secrest" },
        }}
      />
      {/* Hero */}
      <section className="wood-texture overflow-hidden">
        <div className="container py-3 md:py-6">
          <div className="flex flex-row items-center justify-center gap-3 md:gap-8">
            <motion.div initial={{ opacity: 0, x: -20, filter: "blur(6px)" }} animate={{ opacity: 1, x: 0, filter: "blur(0px)" }} transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }} className="text-left md:text-right md:max-w-lg flex-1 min-w-0">
              <h1 className="font-heading text-2xl sm:text-4xl md:text-6xl font-bold uppercase tracking-tight leading-none text-[hsl(var(--nav-foreground))]">
                <span className="block text-[10px] sm:text-base md:text-lg tracking-[0.15em] mb-1 md:mb-2 text-[hsl(var(--nav-foreground))]/80">
                  Of Course It's Good — Bold Family Recipes
                </span>
                Don't Know What's
                <span className="block text-primary">For Dinner?</span>
              </h1>
              <p className="font-body text-[hsl(var(--nav-foreground))]/70 mt-1.5 md:mt-3 text-xs sm:text-sm md:text-base max-w-md">
                Quick, practical recipes for busy days — flavor-packed meals without the extra fuss ✨
              </p>
              <div className="mt-3 md:mt-5 flex flex-wrap gap-1.5 md:gap-3 justify-start md:justify-end">
                <Button asChild size="lg" className="font-heading uppercase tracking-wide rounded-full px-3 h-8 text-[11px] tracking-normal md:tracking-wide md:h-11 md:px-7 md:text-sm">
                  <Link to="/recipes">Try My Recipes</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="font-heading uppercase tracking-wide rounded-full px-3 h-8 text-[11px] tracking-normal md:tracking-wide md:h-11 md:px-7 md:text-sm bg-card/20 border-primary/50 text-primary hover:bg-card/30">
                  <Link to="/cookbooks">Buy My Cookbook</Link>
                </Button>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, rotateY: -30, scale: 0.85, filter: "blur(8px)" }}
              animate={{ opacity: 1, rotateY: -8, scale: 1, filter: "blur(0px)" }}
              transition={{ duration: 0.8, delay: 0.2, type: "spring", stiffness: 80 }}
              whileHover={{ rotateY: 0, scale: 1.04, boxShadow: "0 30px 60px -15px rgba(0,0,0,0.5)" }}
              className="flex-shrink-0"
              style={{ perspective: 1200 }}
            >
              <Link to="/cookbooks" className="block">
                <div
                  className="w-20 sm:w-36 md:w-60 rounded-lg overflow-hidden shadow-[8px_8px_30px_rgba(0,0,0,0.45)] border-2 border-[hsl(var(--nav-foreground))]/10"
                  style={{
                    transformStyle: "preserve-3d",
                    boxShadow: "8px 4px 25px rgba(0,0,0,0.4), inset -2px 0 4px rgba(0,0,0,0.15)",
                  }}
                >
                  <div className="aspect-[3/4] overflow-hidden">
                    <img
                      src={bookCoverFull}
                      alt="Of Course It's Good! cookbook by Jessica Secrest"
                      width="480"
                      height="640"
                      fetchPriority="high"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* As Seen On */}
      <section className="bg-background py-4 md:py-6 border-b border-border overflow-hidden">
        <p className="text-center font-heading uppercase tracking-[0.25em] text-xs text-muted-foreground mb-3 md:mb-4">As Seen On</p>
        {/* Mobile: auto-scrolling single row */}
        <div className="md:hidden relative [mask-image:linear-gradient(to_right,transparent,black_12%,black_88%,transparent)]">
          <div className="flex w-max animate-press-marquee">
            {[0, 1].map((dup) => (
              <div key={dup} className="flex items-center gap-8 pr-8" aria-hidden={dup === 1}>
                {pressOutlets.map((o) => (
                  <Link key={o.domain} to="/press" tabIndex={dup === 1 ? -1 : 0} aria-label={`Jessica featured on ${o.name}`} className="flex items-center gap-2 opacity-70 grayscale whitespace-nowrap">
                    {logoKey && (
                      <img
                        src={`https://img.logo.dev/${o.domain}?token=${logoKey}&size=64&format=png&retina=true`}
                        alt=""
                        width={24}
                        height={24}
                        loading="lazy"
                        className="h-6 w-6 rounded-md object-contain"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    )}
                    <span className="font-heading uppercase tracking-tight text-sm font-bold text-foreground">{o.name}</span>
                  </Link>
                ))}
              </div>
            ))}
          </div>
        </div>
        {/* Desktop: static row */}
        <div className="container hidden md:flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
          {pressOutlets.map((o) => (
            <Link key={o.domain} to="/press" aria-label={`Jessica featured on ${o.name}`} className="flex items-center gap-2 opacity-70 grayscale hover:opacity-100 hover:grayscale-0 transition-all">
              {logoKey && (
                <img
                  src={`https://img.logo.dev/${o.domain}?token=${logoKey}&size=64&format=png&retina=true`}
                  alt=""
                  width={32}
                  height={32}
                  loading="lazy"
                  className="h-8 w-8 rounded-md object-contain"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              )}
              <span className="font-heading uppercase tracking-tight text-base font-bold text-foreground">{o.name}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Latest recipe spotlight — always the newest published recipe */}
      {newRow[0] && (
        <section className="bg-background sprinkle-bg py-6 md:py-8">
          <div className="container">
            <Link
              to={`/recipes/${newRow[0].slug}`}
              className="group flex items-stretch max-w-4xl mx-auto rounded-2xl overflow-hidden bg-card text-foreground border border-border shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
            >
              <div className="relative w-32 sm:w-44 md:w-64 flex-shrink-0 overflow-hidden">
                <img
                  src={optimizeStorageUrl(newRow[0].featured_image_url || foodDinner, 520, 400)}
                  alt={newRow[0].title}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  onError={(e) => { e.currentTarget.src = foodDinner; }}
                />
                <span className="absolute top-2 left-2 bg-primary text-primary-foreground font-heading uppercase text-[10px] tracking-[0.15em] font-bold px-2.5 py-1 rounded-full shadow">
                  New
                </span>
              </div>
              <div className="flex-1 min-w-0 flex flex-col md:flex-row md:items-center gap-3 md:gap-6 p-4 md:p-6 border-l-4 border-primary">
                <div className="flex-1 min-w-0">
                  <p className="font-handwritten text-primary text-xl md:text-2xl leading-none mb-1.5">just posted ✨</p>
                  <h2 className="font-heading text-xl md:text-3xl font-bold uppercase tracking-tight leading-tight text-foreground line-clamp-2">
                    {newRow[0].title}
                  </h2>
                  {((newRow[0].prep_time || 0) + (newRow[0].cook_time || 0)) > 0 && (
                    <p className="text-muted-foreground font-body text-sm mt-1.5">
                      ⏱ Ready in {(newRow[0].prep_time || 0) + (newRow[0].cook_time || 0)} minutes
                    </p>
                  )}
                </div>
                <span className="self-start md:self-center inline-flex items-center gap-1 font-heading uppercase tracking-wide rounded-full bg-foreground text-background group-hover:bg-primary group-hover:text-primary-foreground transition-colors px-5 py-2 text-xs md:text-sm whitespace-nowrap">
                  Get the Recipe →
                </span>
              </div>
            </Link>
          </div>
        </section>
      )}



      {/* Browse Popular Categories */}
      <section className="bg-background sprinkle-bg py-8 md:py-10">
        <div className="container">
          <ScrollReveal>
            <h2 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight mb-6">
              Browse Popular Categories
            </h2>
          </ScrollReveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            {categoriesMeta.map((cat, i) => {
              const rawImg = categoryCardImages[cat.link] || categoryFallbacks[cat.link] || foodDinner;
              const imgSrc = optimizeStorageUrl(rawImg, 400, 400);
              return (
                <ScrollReveal key={cat.name} delay={i * 0.06} className={(cat as any).mobileOnly ? "lg:hidden" : ""}>
                  <Link to={`/recipes?category=${cat.link}`}>
                    <div className="group">
                      <div className="relative rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                        <div className="aspect-square overflow-hidden">
                          <img src={imgSrc} alt={`${cat.name} recipes`} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" onError={(e) => { e.currentTarget.src = categoryFallbacks[cat.link] || foodDinner; }} />
                        </div>
                        <span className="cat-label">{cat.name}</span>
                      </div>
                      <p className="text-muted-foreground font-body text-xs mt-2 text-center">{cat.desc}</p>
                    </div>
                  </Link>
                </ScrollReveal>
              );
            })}
            {extraHubs.map((hub, i) => (
              <ScrollReveal key={hub.name} delay={(categoriesMeta.length + i) * 0.06} className={i === extraHubs.length - 1 ? "col-span-2 md:col-span-1" : ""}>
                <Link to={hub.href}>
                  <div className="group">
                    <div className="relative rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                      <div className={`${i === extraHubs.length - 1 ? "aspect-[2/1] md:aspect-square" : "aspect-square"} overflow-hidden`}>
                        <img src={hub.img} alt={`${hub.name} recipes`} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                      </div>
                      <span className="cat-label">{hub.name}</span>
                    </div>
                    <p className="text-muted-foreground font-body text-xs mt-2 text-center">{hub.desc}</p>
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>
          <ScrollReveal className="text-center mt-8">
            <Button asChild variant="outline" className="font-heading uppercase tracking-wide rounded-full border-foreground/60 text-foreground hover:bg-foreground/5">
              <Link to="/recipes">Explore More Recipes →</Link>
            </Button>
          </ScrollReveal>
        </div>
      </section>

      <AdSlot id="home-top" minHeight={250} />

      {renderRecipeRow("hot off the stove 🔥", "New Recipes", "The freshest drops from my kitchen.", newRow, "bg-card")}
      {renderRecipeRow("everyone's making these 📈", "Trending Recipes", "What people are cooking right now.", trendingRow, "wood-texture", true)}
      {renderRecipeRow("reader favorites ⭐", "Top Recipes", "Most-loved by readers — top reviews & comments.", topRow, "bg-background sprinkle-bg")}

      <AdSlot id="home-mid" minHeight={250} />

      <HomepageSeasonalBlock />

      {/* Upcoming Events */}
      {upcomingEvents.length > 0 && (
        <section className="bg-card py-8 md:py-10 border-t border-border">
          <div className="container">
            <ScrollReveal className="text-center mb-6">
              <p className="font-handwritten text-primary text-2xl mb-1">catch me irl 🎟️</p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight mb-2">Upcoming Events</h2>
              <p className="text-muted-foreground font-body text-sm max-w-lg mx-auto">
                Book signings, cooking classes, and everything in between.
              </p>
            </ScrollReveal>
            <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
              {upcomingEvents.map((ev: any, i: number) => (
                <ScrollReveal key={ev.id} delay={i * 0.08}>
                  <Link to="/events" className="block group">
                    <div className="rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all bg-background flex">
                      {ev.image_url && (
                        <div className="w-32 md:w-40 flex-shrink-0 aspect-square overflow-hidden">
                          <img src={optimizeStorageUrl(ev.image_url, 320, 320)} alt={ev.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                        </div>
                      )}
                      <div className="p-4 flex-1">
                        <p className="text-xs text-primary font-heading uppercase tracking-wider mb-1">
                          {new Date(ev.event_date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                        </p>
                        <h3 className="font-heading text-base font-bold uppercase tracking-tight line-clamp-2 group-hover:text-primary transition-colors">
                          {ev.title}
                        </h3>
                        {ev.location && <p className="text-xs text-muted-foreground font-body mt-1 line-clamp-1">{ev.location}</p>}
                      </div>
                    </div>
                  </Link>
                </ScrollReveal>
              ))}
            </div>
            <ScrollReveal className="text-center mt-6">
              <Button asChild variant="outline" className="font-heading uppercase tracking-wide rounded-full border-foreground/60 text-foreground hover:bg-foreground/5 text-sm">
                <Link to="/events">See All Events →</Link>
              </Button>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* Who Am I */}
      <section className="bg-warm-tan py-8 md:py-10">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-6 md:gap-10 items-center max-w-5xl mx-auto">
            <ScrollReveal direction="left">
              <div className="rounded-2xl overflow-hidden shadow-lg max-w-xs mx-auto">
                <img src={jessAbout} alt="Jessica Secrest" loading="lazy" width="400" height="500" className="w-full h-auto object-cover" />
              </div>
            </ScrollReveal>
            <ScrollReveal direction="right" className="text-center md:text-left">
              <p className="font-handwritten text-primary text-2xl mb-1">nice to meet ya!</p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight mb-3">Who Am I?</h2>
              <p className="text-muted-foreground font-body leading-relaxed mb-3 text-sm">
                I'm a food blogger and creator who started making chaotic cooking videos and accidentally built a community of millions. I'm the unapologetically bold voice behind <SocialLinksPopover />. 🍳
              </p>
              <p className="text-muted-foreground font-body leading-relaxed mb-4 text-sm">
                Cooking should be fun, food should taste amazing, and you don't need a culinary degree to nail dinner. That's my whole thing.
              </p>
              <Button asChild variant="outline" className="font-heading uppercase tracking-wide rounded-full border-foreground/60 text-foreground hover:bg-foreground/5 text-sm">
                <Link to="/about">Learn More About Me →</Link>
              </Button>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Cookbook Section */}
      <section className="wood-texture py-8 md:py-10">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-6 md:gap-10 items-center max-w-5xl mx-auto">
            <ScrollReveal direction="left">
              <p className="font-handwritten text-primary text-2xl mb-1">my debut cookbook!</p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight mb-2 text-[hsl(var(--nav-foreground))]">Of Course It's Good!</h2>
              <p className="font-heading text-base md:text-lg uppercase tracking-wide text-[hsl(var(--nav-foreground))]/70 mb-3">
                Aggressively Delicious Meals <span className="text-primary font-bold">ANYONE</span> Can Make and <span className="text-primary font-bold">EVERYONE</span> Will Love
              </p>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-primary">
                  <img src={jessSalad} alt="Jessica Secrest" className="w-full h-full object-cover" />
                </div>
                <div>
                  <p className="font-heading uppercase text-sm font-bold tracking-wide text-[hsl(var(--nav-foreground))]">Jessica Secrest</p>
                  <p className="text-primary text-xs font-body">Creator of <SocialLinksPopover /></p>
                </div>
              </div>
              <Button asChild size="lg" className="font-heading uppercase tracking-wide rounded-full px-7">
                <Link to="/cookbooks">Buy My Cookbook</Link>
              </Button>
            </ScrollReveal>
            <ScrollReveal direction="right">
              <div className="w-full max-w-xs mx-auto md:ml-auto aspect-[3/4] rounded-2xl overflow-hidden shadow-lg">
                <img src={cookbookCover} alt="Of Course It's Good cookbook" className="w-full h-full object-cover" />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <AdSlot id="home-cookbook" minHeight={250} />

      {/* Roundups */}
      {roundupPosts.length > 0 && (
        <section className="bg-background sprinkle-bg py-8 md:py-10 border-t border-border">
          <div className="container">
            <ScrollReveal className="text-center mb-6">
              <p className="font-handwritten text-primary text-2xl mb-1">bundle up 🎁</p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight mb-2">Recipe Roundups</h2>
              <p className="text-muted-foreground font-body text-sm max-w-lg mx-auto">
                Themed collections when you don't just want one recipe — you want the whole plan.
              </p>
            </ScrollReveal>
            <div className="grid md:grid-cols-3 gap-4 md:gap-6">
              {roundupPosts.map((post: any, i: number) => (
                <ScrollReveal key={post.id} delay={i * 0.08}>
                  <Link to={`/blog/${post.slug}`} className="block group">
                    <div className="rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all bg-card">
                      <div className="aspect-[16/10] overflow-hidden">
                        <img src={optimizeStorageUrl(post.featured_image_url || foodDinner, 500, 313)} alt={post.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" onError={(e) => { e.currentTarget.src = foodDinner; }} />
                      </div>
                      <div className="p-4">
                        <p className="text-[10px] text-primary font-heading uppercase tracking-wider mb-1">
                          {Array.isArray(post.roundup_recipe_slugs) ? `${post.roundup_recipe_slugs.length} recipes` : "Roundup"}
                        </p>
                        <h3 className="font-heading text-sm font-bold uppercase tracking-tight line-clamp-2 group-hover:text-primary transition-colors">
                          {post.title}
                        </h3>
                        {post.excerpt && <p className="text-muted-foreground font-body text-xs mt-1 line-clamp-2">{post.excerpt}</p>}
                      </div>
                    </div>
                  </Link>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Blog Preview - Dynamic */}
      {blogPosts.length > 0 && (
        <section className="bg-card py-8 md:py-10 border-t border-border">
          <div className="container">
            <ScrollReveal className="text-center mb-8">
              <p className="font-handwritten text-primary text-2xl mb-1">stories & tips ✍️</p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight mb-2">My Life On The Page</h2>
              <p className="text-muted-foreground font-body text-sm max-w-lg mx-auto">
                Behind-the-scenes stories, kitchen tips, and the kind of real talk you won't find in a recipe card.
              </p>
            </ScrollReveal>
            <div className="grid md:grid-cols-3 gap-4 md:gap-6">
              {blogPosts.map((post, i) => (
                <ScrollReveal key={post.id} delay={i * 0.08}>
                  <Link to={`/blog/${post.slug}`} className="block group">
                    <div className="rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 bg-background">
                      <div className="aspect-[16/10] overflow-hidden">
                        <img src={optimizeStorageUrl(post.featured_image_url || foodBreakfast, 500, 313)} alt={post.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" onError={e => { e.currentTarget.src = foodBreakfast; }} />
                      </div>
                      <div className="p-4">
                        <h3 className="font-heading text-sm font-bold uppercase tracking-tight line-clamp-2 group-hover:text-primary transition-colors">
                          {post.title}
                        </h3>
                        {(() => {
                          const blurb = post.excerpt?.trim() || (post.content ? post.content.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 160) : '');
                          return blurb ? <p className="text-muted-foreground font-body text-xs mt-1 line-clamp-2">{blurb}</p> : null;
                        })()}
                      </div>
                    </div>
                  </Link>
                </ScrollReveal>
              ))}
            </div>
            <ScrollReveal className="text-center mt-6">
              <Button asChild variant="outline" className="font-heading uppercase tracking-wide rounded-full border-foreground/60 text-foreground hover:bg-foreground/5 text-sm">
                <Link to="/blog">Read My Blog →</Link>
              </Button>
            </ScrollReveal>
          </div>
        </section>
      )}

      <AdSlot id="home-blog" minHeight={250} />

      {/* Reader Reviews */}
      {recentReviews.length > 0 && (
        <section className="bg-warm-tan py-8 md:py-10">
          <div className="container">
            <ScrollReveal className="text-center mb-6">
              <p className="font-handwritten text-primary text-2xl mb-1">from the reviews ⭐</p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight mb-2">What Readers Are Saying</h2>
            </ScrollReveal>
            <div className="grid md:grid-cols-3 gap-4 max-w-5xl mx-auto">
              {recentReviews.map((r: any, i: number) => (
                <ScrollReveal key={r.id} delay={i * 0.08}>
                  <Link to={`/recipes/${r.recipe.slug}`} className="block group h-full">
                    <div className="rounded-2xl bg-card p-5 shadow-md hover:shadow-xl transition-all h-full flex flex-col">
                      <StarRatingDisplay value={r.rating} size="sm" />
                      <p className="text-sm font-body text-foreground mt-2 line-clamp-4 flex-1">
                        "{(r.content || "").trim()}"
                      </p>
                      <div className="mt-3 pt-3 border-t border-border">
                        <p className="text-xs text-muted-foreground font-body">— {r.author_name || "A reader"}</p>
                        <p className="text-xs text-primary font-heading uppercase tracking-wider mt-0.5 group-hover:underline">
                          on {r.recipe.title}
                        </p>
                      </div>
                    </div>
                  </Link>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Shop strip */}
      {shopStrip.length > 0 && (
        <section className="bg-card py-8 md:py-10 border-t border-border">
          <div className="container">
            <ScrollReveal className="text-center mb-6">
              <p className="font-handwritten text-primary text-2xl mb-1">the good stuff 🛒</p>
              <Link to="/shop" className="inline-block hover:text-primary transition-colors">
                <h2 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight mb-2">Shop My Kitchen</h2>
              </Link>
              <p className="text-muted-foreground font-body text-sm max-w-lg mx-auto">
                The tools, gadgets, and pantry picks I actually use.
              </p>
            </ScrollReveal>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {shopStrip.slice(0, 4).map((p: any, i: number) => (
                <ScrollReveal key={p.id} delay={i * 0.06}>
                  <a href={p.affiliate_url} target="_blank" rel="noopener noreferrer external nofollow sponsored" className="block group">
                    <div className="rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all bg-background">
                      <div className="aspect-square overflow-hidden">
                        <img src={optimizeStorageUrl(p.image_url, 400, 400)} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                      </div>
                      <div className="p-3">
                        <p className="font-heading text-xs font-bold uppercase tracking-tight line-clamp-2 group-hover:text-primary transition-colors">{p.name}</p>
                      </div>
                    </div>
                  </a>
                </ScrollReveal>
              ))}
            </div>
            <ScrollReveal className="text-center mt-6">
              <Button asChild variant="outline" className="font-heading uppercase tracking-wide rounded-full border-foreground/60 text-foreground hover:bg-foreground/5 text-sm">
                <Link to="/shop">Shop More →</Link>
              </Button>
            </ScrollReveal>
            <p className="text-[10px] text-muted-foreground font-body text-center mt-3">Contains affiliate links — I may earn a small commission.</p>
          </div>
        </section>
      )}

      {/* Press */}
      <section className="bg-warm-tan py-8 md:py-10">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-6 md:gap-10 items-center max-w-5xl mx-auto">
            <ScrollReveal direction="left">
              <p className="font-handwritten text-primary text-2xl mb-1">as seen on tv! 📺</p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight mb-3">Where I Have Been Featured</h2>
              <p className="text-muted-foreground font-body leading-relaxed mb-4 text-sm">
                From Good Morning America to The Drew Barrymore Show — bold cooking energy on the big screen!
              </p>
              <Button asChild variant="outline" className="font-heading uppercase tracking-wide rounded-full border-foreground/60 text-foreground hover:bg-foreground/5 text-sm">
                <Link to="/press">Explore Now →</Link>
              </Button>
            </ScrollReveal>
            <ScrollReveal direction="right">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-lg max-w-xs ml-auto">
                <img src={jessGma} alt="Jess on TV" loading="lazy" className="w-full h-full object-cover" />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Partnerships */}
      <section className="bg-background sprinkle-bg py-8 md:py-10 border-t border-border">
        <ScrollReveal className="container text-center max-w-xl">
          <p className="font-handwritten text-primary text-2xl mb-1">let's collab! 🤝</p>
          <h2 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight mb-3">Let's Create Together</h2>
          <p className="text-muted-foreground font-body leading-relaxed mb-5 text-sm">
            Bold, real, and made for people who love simple, delicious cooking. Let's make something big together.
          </p>
          <Button asChild variant="outline" className="font-heading uppercase tracking-wide rounded-full border-foreground/60 text-foreground hover:bg-foreground/5">
            <Link to="/contact?tab=partnerships">Partner With Me →</Link>
          </Button>
        </ScrollReveal>
      </section>

      {/* Newsletter */}
      <section className="bg-[hsl(var(--nav-bg))] py-8 md:py-10">
        <ScrollReveal className="container max-w-md text-center">
          <Heart className="h-7 w-7 text-primary mx-auto mb-3" />
          <h2 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight mb-2 text-[hsl(var(--nav-foreground))]">
            Subscribe For <span className="text-primary">Insights</span>
          </h2>
          <p className="text-[hsl(var(--nav-foreground))]/60 font-body mb-5 text-sm">
            Sign up for my monthly newsletter — it's like getting a Christmas card from me EVERY month! 💌
          </p>
          {nlDone ? (
            <p className="font-handwritten text-primary text-2xl">you're on the list! 🤠</p>
          ) : (
            <form onSubmit={handleNewsletterSubmit} className="space-y-2">
              <label htmlFor="home-newsletter-name" className="sr-only">Your name</label>
              <Input id="home-newsletter-name" name="name" autoComplete="name" aria-label="Your name" type="text" placeholder="Your name" value={nlName} onChange={e => setNlName(e.target.value)} required className="bg-[hsl(var(--nav-foreground))]/10 border-[hsl(var(--nav-foreground))]/20 text-[hsl(var(--nav-foreground))] placeholder:text-[hsl(var(--nav-foreground))]/40 rounded-full text-sm" />
              <div className="flex flex-col sm:flex-row gap-2">
                <label htmlFor="home-newsletter-email" className="sr-only">Email address</label>
                <Input id="home-newsletter-email" name="email" autoComplete="email" aria-label="Email address" type="email" placeholder="Enter your email" value={nlEmail} onChange={e => setNlEmail(e.target.value)} required className="flex-1 bg-[hsl(var(--nav-foreground))]/10 border-[hsl(var(--nav-foreground))]/20 text-[hsl(var(--nav-foreground))] placeholder:text-[hsl(var(--nav-foreground))]/40 rounded-full text-sm" />
                <Button type="submit" disabled={nlSubmitting} aria-label="Subscribe to newsletter" className="font-heading uppercase tracking-wide rounded-full px-6 text-sm">
                  {nlSubmitting ? "..." : "Subscribe"}
                </Button>
              </div>
            </form>
          )}
          <p className="text-[hsl(var(--nav-foreground))]/40 text-xs font-body mt-3">info@ofcourseitsgood.com</p>
          <p className="font-handwritten text-primary text-2xl mt-2 rotate-[-2deg]">xo, Jess ♡</p>
        </ScrollReveal>
      </section>
    </div>
  );
};

export default Index;
